import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Grid, Paper, Typography, Card, CardContent, Tabs, Tab, Select,
    MenuItem, FormControl, InputLabel, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Skeleton, Chip, IconButton,
    Tooltip, Divider, LinearProgress, Alert, alpha,
} from '@mui/material';
import {
    AttachMoney, People, AirplanemodeActive, Receipt,
    TrendingUp, TrendingDown, TrendingFlat, Refresh,
    BarChart as BarChartIcon, PieChart as PieChartIcon,
    TableChart, CalendarToday, ErrorOutline,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchStatistiques,
    fetchStatistiquesPrev,
    fetchVentesAnnuelles,
} from '../../data/slices/statistiquesSlice';
import BarChart from '../../components/charts/BarChart';
import PieChart from '../../components/charts/PieChart';
import { COLORS } from '../../settings/themeSettings';

// ─── SSR-safe ────────────────────────────────────────────────────────────────
const ClientOnly = ({ children }) => {
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => { setMounted(true); return () => setMounted(false); }, []);
    return mounted ? children : null;
};

// ─── Skeletons ────────────────────────────────────────────────────────────────
const ChartSkeleton = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant="text" width="55%" height={26} />
        <Skeleton variant="rounded" width="100%" sx={{ flex: 1, minHeight: 300 }} />
    </Box>
);
const KpiSkeleton = () => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Skeleton variant="circular" width={52} height={52} />
                <Box flex={1}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="45%" height={40} />
                </Box>
            </Box>
            <Skeleton variant="rounded" height={5} />
        </CardContent>
    </Card>
);

// ─── Trend chip ───────────────────────────────────────────────────────────────
const TrendChip = ({ current = 0, prev = 0 }) => {
    if (prev === 0 && current === 0) return null;
    const pct = prev === 0 ? 100 : Math.round(((current - prev) / prev) * 100);
    const up = pct > 0;
    const flat = pct === 0;
    return (
        <Tooltip title={`vs période précédente (${prev.toLocaleString('fr-FR')})`} arrow>
            <Chip
                size="small"
                icon={flat ? <TrendingFlat sx={{ fontSize: 14 }} /> : up ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
                label={flat ? 'Stable' : `${up ? '+' : ''}${pct}%`}
                sx={{
                    fontWeight: 700, fontSize: '0.68rem', border: 'none',
                    bgcolor: flat ? alpha(COLORS.tertiary, 0.1) : up ? alpha(COLORS.success, 0.1) : alpha(COLORS.error, 0.1),
                    color: flat ? COLORS.tertiary : up ? COLORS.success : COLORS.error,
                }}
            />
        </Tooltip>
    );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, icon, color, current, prev, progress }) => (
    <Card sx={{
        height: '100%', borderTop: `4px solid ${color}`,
        transition: 'transform .2s, box-shadow .2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
    }}>
        <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Box sx={{ bgcolor: alpha(color, 0.12), borderRadius: 2, width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
                    {icon}
                </Box>
                {prev !== undefined && current !== undefined && (
                    <TrendChip current={current} prev={prev} />
                )}
            </Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.8} display="block">
                {title}
            </Typography>
            <Typography variant="h5" fontWeight={800} mt={0.5} mb={1} lineHeight={1.2}>
                {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
            </Typography>
            {progress !== undefined && (
                <LinearProgress variant="determinate" value={Math.min(progress, 100)}
                    sx={{ height: 5, borderRadius: 5, bgcolor: alpha(color, 0.15), '& .MuiLinearProgress-bar': { background: color } }} />
            )}
        </CardContent>
    </Card>
);

// ─── Chip état circuit ────────────────────────────────────────────────────────
const EtatChip = ({ etat }) => {
    const cfg = {
        confirme: { label: 'Confirmé', color: 'success' },
        confirmé: { label: 'Confirmé', color: 'success' },
        en_cours: { label: 'En cours', color: 'warning' },
        annule: { label: 'Annulé', color: 'error' },
        annulé: { label: 'Annulé', color: 'error' },
        termine: { label: 'Terminé', color: 'default' },
        terminé: { label: 'Terminé', color: 'default' },
    }[(etat || '').toLowerCase()] || { label: etat || '—', color: 'default' };
    return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 600 }} />;
};

// ─── Section header ───────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
    <Typography variant="caption" fontWeight={700} color="text.secondary"
        textTransform="uppercase" letterSpacing={1} display="block" mb={1.5}>
        {children}
    </Typography>
);

// ─── État vide ────────────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
        height="100%" minHeight={240} gap={2} sx={{ opacity: 0.55 }}>
        <BarChartIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
        <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
);

// ════════════════════════════════════════════════════════════════════════════════
const StatistiquesPage = () => {
    const dispatch = useDispatch();

    // ── Redux state ───────────────────────────────────────────────────────────
    const {
        current,
        prev,
        ventesParMois,
        loading,
        loadingPrev,
        loadingVentes,
        error,
        lastUpdated,
    } = useSelector((s) => s.statistiques);

    const [timeRange, setTimeRange] = useState('annee');
    const [activeTab, setActiveTab] = useState(0);

    // ── Chargement initial & sur changement de période ────────────────────────
    const loadAll = useCallback((range) => {
        dispatch(fetchStatistiques(range));
        dispatch(fetchStatistiquesPrev(range));
        dispatch(fetchVentesAnnuelles());
    }, [dispatch]);

    useEffect(() => {
        loadAll(timeRange);
    }, [timeRange, loadAll]);

    const handleRefresh = () => loadAll(timeRange);

    const isLoading = loading || loadingVentes;

    // ── Données chartVentes : mensuelles si dispo, sinon vide ─────────────────
    const chartVentes = useMemo(() => {
        if (ventesParMois && ventesParMois.length > 0) return ventesParMois;
        return [];
    }, [ventesParMois]);

    const periodLabel = timeRange === 'mois' ? 'ce mois-ci' : 'cette année';
    const lastUpdateDate = lastUpdated ? new Date(lastUpdated) : null;

    // ── CA progress : part du mois sur l'année (si annee) ────────────────────
    const caProgress = useMemo(() => {
        if (!current || !prev) return 0;
        const total = (current.chiffreAffaires || 0) + (prev.chiffreAffaires || 0);
        return total > 0 ? (current.chiffreAffaires / total) * 100 : 0;
    }, [current, prev]);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>

            {/* ── En-tête ─────────────────────────────────────────────────────── */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4" component="h1" fontWeight={800} color={COLORS.secondary}>
                        Tableau de Bord
                    </Typography>
                    {lastUpdateDate && (
                        <Box display="flex" alignItems="center" gap={0.8} mt={0.4}>
                            <CalendarToday sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                                Mis à jour le {lastUpdateDate.toLocaleDateString('fr-FR')} à{' '}
                                {lastUpdateDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box display="flex" alignItems="center" gap={1.5}>
                    <Tooltip title="Actualiser" arrow>
                        <IconButton onClick={handleRefresh} disabled={isLoading}
                            sx={{ bgcolor: alpha(COLORS.primary, 0.08), '&:hover': { bgcolor: alpha(COLORS.primary, 0.16) } }}>
                            <Refresh sx={{ color: COLORS.primary }} />
                        </IconButton>
                    </Tooltip>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 155 }}>
                        <InputLabel>Période</InputLabel>
                        <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} label="Période">
                            <MenuItem value="mois">Ce mois</MenuItem>
                            <MenuItem value="annee">Cette année</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Barre de progression globale */}
            {isLoading && <LinearProgress sx={{ mb: 2, borderRadius: 2, height: 3 }} />}

            {/* Erreur API */}
            {error && !isLoading && (
                <Alert severity="error" icon={<ErrorOutline />} sx={{ mb: 3 }}
                    action={<IconButton size="small" onClick={handleRefresh}><Refresh fontSize="small" /></IconButton>}>
                    Impossible de charger les données : {typeof error === 'string' ? error : 'Vérifiez votre connexion au serveur.'}
                </Alert>
            )}

            {/* ── KPI Cards ────────────────────────────────────────────────────── */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {isLoading ? Array(4).fill(0).map((_, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}><KpiSkeleton /></Grid>
                )) : current ? (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                title="Total Dossiers"
                                value={current.totalDossiers}
                                icon={<Receipt fontSize="medium" />}
                                color={COLORS.primary}
                                current={current.totalDossiers}
                                prev={prev?.totalDossiers}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                title="Clients"
                                value={current.totalClients}
                                icon={<People fontSize="medium" />}
                                color={COLORS.secondary}
                                current={current.totalClients}
                                prev={prev?.totalClients}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                title="Billets Émis"
                                value={current.totalBillets}
                                icon={<AirplanemodeActive fontSize="medium" />}
                                color={COLORS.success}
                                current={current.totalBillets}
                                prev={prev?.totalBillets}
                                progress={current.totalDossiers > 0 ? (current.totalBillets / current.totalDossiers) * 100 : 0}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                title="Chiffre d'Affaires"
                                value={`${(current.chiffreAffaires || 0).toLocaleString('fr-FR')} FCFA`}
                                icon={<AttachMoney fontSize="medium" />}
                                color={COLORS.warning}
                                current={current.chiffreAffaires}
                                prev={prev?.chiffreAffaires}
                                progress={caProgress}
                            />
                        </Grid>
                    </>
                ) : !error && (
                    Array(4).fill(0).map((_, i) => (
                        <Grid item xs={12} sm={6} md={3} key={i}><KpiSkeleton /></Grid>
                    ))
                )}
            </Grid>

            {/* ── Onglets ───────────────────────────────────────────────────────── */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                    indicatorColor="primary" textColor="primary"
                    variant="scrollable" scrollButtons="auto"
                    sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab icon={<BarChartIcon fontSize="small" />} iconPosition="start" label="Vue d'ensemble" />
                    <Tab icon={<TableChart fontSize="small" />} iconPosition="start" label="Dossiers récents" />
                    <Tab icon={<PieChartIcon fontSize="small" />} iconPosition="start" label="Répartition activité" />
                </Tabs>

                <Box sx={{ p: 2.5 }}>

                    {/* ── TAB 0 : Vue d'ensemble ──────────────────────────────── */}
                    {activeTab === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={7}>
                                {isLoading || loadingVentes ? <ChartSkeleton /> : (
                                    <>
                                        <SectionTitle>Évolution mensuelle — {periodLabel}</SectionTitle>
                                        <Box sx={{ height: 370 }}>
                                            <ClientOnly>
                                                <React.Suspense fallback={<ChartSkeleton />}>
                                                    {chartVentes.some(d => d.value > 0) ? (
                                                        <BarChart identifier="bar-stat-ventes" data={chartVentes} />
                                                    ) : (
                                                        <EmptyState message={`Aucune donnée Mensuelle ${periodLabel}`} />
                                                    )}
                                                </React.Suspense>
                                            </ClientOnly>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                            <Grid item xs={12} md={5}>
                                {isLoading ? <ChartSkeleton /> : (
                                    <>
                                        <SectionTitle>Répartition par activité</SectionTitle>
                                        <Box sx={{ height: 370 }}>
                                            <ClientOnly>
                                                <React.Suspense fallback={<ChartSkeleton />}>
                                                    {current?.repartitionActivite?.length > 0 ? (
                                                        <PieChart identifier="pie-stat-activite"
                                                            data={current.repartitionActivite} />
                                                    ) : (
                                                        <EmptyState message={`Aucune répartition ${periodLabel}`} />
                                                    )}
                                                </React.Suspense>
                                            </ClientOnly>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {/* ── TAB 1 : Dossiers récents ───────────────────────────── */}
                    {activeTab === 1 && (
                        <Grid container spacing={3}>
                            {/* Circuits récents */}
                            <Grid item xs={12} md={6}>
                                <SectionTitle>Derniers Circuits</SectionTitle>
                                {isLoading ? <Skeleton variant="rounded" height={300} /> : (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: alpha(COLORS.primary, 0.06) }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Référence</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Départ</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>État</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {current?.recentCircuits?.length > 0
                                                    ? current.recentCircuits.map((row) => (
                                                        <TableRow key={row.id} hover>
                                                            <TableCell>
                                                                <Typography variant="caption" fontWeight={700} color="primary">
                                                                    {row.reference || `#${row.id}`}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {row.nom || '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {row.dateDepart ? new Date(row.dateDepart).toLocaleDateString('fr-FR') : '—'}
                                                            </TableCell>
                                                            <TableCell><EtatChip etat={row.etat} /></TableCell>
                                                        </TableRow>
                                                    ))
                                                    : (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                                                                Aucun circuit récent
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Grid>

                            {/* Billets récents */}
                            <Grid item xs={12} md={6}>
                                <SectionTitle>Derniers Billets</SectionTitle>
                                {isLoading ? <Skeleton variant="rounded" height={300} /> : (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: alpha(COLORS.success, 0.06) }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Référence</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Passager</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Émission</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }} align="right">Montant</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {current?.recentBillets?.length > 0
                                                    ? current.recentBillets.map((row) => (
                                                        <TableRow key={row.id} hover>
                                                            <TableCell>
                                                                <Typography variant="caption" fontWeight={700} color="success.main">
                                                                    {row.reference || row.pnr || `#${row.id}`}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {row.passager || row.nom_passager || '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {row.date_emission ? new Date(row.date_emission).toLocaleDateString('fr-FR') : '—'}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="caption" fontWeight={700}>
                                                                    {row.prix_total ? `${Number(row.prix_total).toLocaleString('fr-FR')} F` : '—'}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                    : (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                                                                Aucun billet récent
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                            </Grid>

                            {/* Résumé */}
                            {current && (
                                <Grid item xs={12}>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box display="flex" gap={2} flexWrap="wrap">
                                        <Chip icon={<Receipt fontSize="small" />}
                                            label={`${current.totalCircuits} circuits`}
                                            variant="outlined" color="primary" />
                                        <Chip icon={<AirplanemodeActive fontSize="small" />}
                                            label={`${current.totalBillets} billets`}
                                            variant="outlined" color="success" />
                                        <Chip icon={<AttachMoney fontSize="small" />}
                                            label={`CA : ${(current.chiffreAffaires || 0).toLocaleString('fr-FR')} FCFA`}
                                            variant="outlined" color="warning" />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {/* ── TAB 2 : Répartition activité ───────────────────────── */}
                    {activeTab === 2 && (
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <SectionTitle>Répartition par service — {periodLabel}</SectionTitle>
                                <Box sx={{ height: 380 }}>
                                    <ClientOnly>
                                        <React.Suspense fallback={<ChartSkeleton />}>
                                            {current?.repartitionActivite?.length > 0 ? (
                                                <PieChart identifier="pie-stat-comparaison"
                                                    data={current.repartitionActivite} donut={false} />
                                            ) : (
                                                <EmptyState message="Aucune donnée disponible" />
                                            )}
                                        </React.Suspense>
                                    </ClientOnly>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <SectionTitle>Résumé comparatif</SectionTitle>
                                <Paper variant="outlined" sx={{ p: 3 }}>
                                    {current ? (
                                        <>
                                            <Grid container spacing={2} mb={2}>
                                                {[
                                                    { label: 'Billets', value: current.totalBillets, color: COLORS.success, icon: <AirplanemodeActive /> },
                                                    { label: 'Circuits', value: current.totalCircuits, color: COLORS.primary, icon: <Receipt /> },
                                                    { label: 'Excursions', value: current.totalExcursions, color: COLORS.warning, icon: <PieChartIcon /> },
                                                    { label: 'Séjours', value: current.totalSejours, color: COLORS.info, icon: <CalendarToday /> },
                                                ].map(({ label, value, color, icon }) => (
                                                    <Grid item xs={6} key={label}>
                                                        <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(color, 0.07) }}>
                                                            {React.cloneElement(icon, { sx: { color, mb: 0.5, fontSize: 28 } })}
                                                            <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>

                                            <Divider sx={{ my: 2 }} />

                                            {current.repartitionActivite.map(({ category, value }) => {
                                                const total = current.chiffreAffaires || 1;
                                                const pct = Math.round((value / total) * 100);
                                                return (
                                                    <Box key={category} mb={1.5}>
                                                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                            <Typography variant="body2" color="text.secondary">{category}</Typography>
                                                            <Typography variant="body2" fontWeight={700}>
                                                                {value.toLocaleString('fr-FR')} FCFA ({pct}%)
                                                            </Typography>
                                                        </Box>
                                                        <LinearProgress variant="determinate" value={pct}
                                                            sx={{ height: 7, borderRadius: 4, bgcolor: alpha(COLORS.primary, 0.1), '& .MuiLinearProgress-bar': { bgcolor: COLORS.primary } }} />
                                                    </Box>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <EmptyState message="Données en cours de chargement…" />
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default StatistiquesPage;
